import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import moment from "moment";
import { addDays, addMonths } from "date-fns";
import Colors from "assets/Style/Colors";
import DatePicker from "components/DatePicker";
import InputField from "components/Input";
import LabelCustomInput from "components/Input/LabelCustomInput";
import SelectField from "components/Select";
import UploadFile from "components/UploadFile";
import UploadFileSingle from "components/UploadFileSingle";
import { showErrorToast, showPromiseToast } from "components/NewToaster";
import { ErrorToaster } from "components/Toaster";
import CustomerServices from "services/Customer";
import SystemServices from "services/System";
import VisaServices from "services/Visa";
import instance from "config/axios";
import routes from "services/System/routes";
import { CleanTypes, Debounce2 } from "utils";

const allowFilesType = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "application/pdf",
  "application/vnd.ms-excel",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

// Same document list as the visa request form
const defaultDocuments = [
  { name: "Employee Undertaking", key: "undertaking", is_required: true },
  { name: "Company Undertaking", key: "cundertaking", is_required: true },
  { name: "Passport Copy", key: "passportcopy", is_required: true },
  { name: "Digital Photo", key: "digitalphoto", is_required: true },
  { name: "Employment Contract", key: "contract", is_required: true },
  { name: "Offer Letter", key: "offerletter", is_required: true },
  { name: "Previous Emirates Ids", key: "emiratesids", is_required: false },
  { name: "Previous UAE Visa Copy", key: "uaevisa", is_required: false },
  { name: "Cancellation", key: "cancellation", is_required: false },
  { name: "UAE Driving License", key: "drivinglicense", is_required: false },
  { name: "Work Permit", key: "workpermit", is_required: false },
  { name: "Other Documents", key: "otherdoc", is_required: false },
];

// Candidate's saved documents laid over the default list (extra saved documents are kept)
const mergeDocuments = (saved = []) => {
  const toDoc = (doc) => ({
    name: doc.name,
    key: doc.key,
    path: doc.path || "",
    expiry_date: doc.expiry_date || null,
    is_required: !!doc.is_required,
  });
  const merged = defaultDocuments.map((doc) => {
    const found = saved.find((item) => item?.key === doc.key);
    return found ? toDoc({ ...found, name: found.name || doc.name, is_required: doc.is_required }) : toDoc(doc);
  });
  saved
    .filter((item) => !defaultDocuments.some((doc) => doc.key === item?.key))
    .forEach((item) => merged.push(toDoc(item)));
  return merged;
};

const isTwoYear = (tenure) => String(tenure || "").includes("2");

// Renewal request with the same candidate details as a visa request.
// Candidate name and visa type (In / Out) are locked, everything else is editable.
function RenewVisaDialog({ open, onClose, selectedItem, onSaved }) {
  const { register, handleSubmit, setValue, getValues, reset, formState: { errors } } = useForm();

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [visaTenure, setVisaTenure] = useState("1 year");
  const [skipMedical, setSkipMedical] = useState(false);
  const [skipEid, setSkipEid] = useState(false);
  const [requestDate, setRequestDate] = useState(new Date());
  const [passportExpiry, setPassportExpiry] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [renewalDocument, setRenewalDocument] = useState("");
  const [docLoader, setDocLoader] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [passportError, setPassportError] = useState(false);

  const getCountries = async () => {
    try {
      const { data } = await SystemServices.getCountries();
      setCountries(data?.countries?.rows);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const getCandidateDetail = async (id) => {
    setLoading(true);
    try {
      const { data } = await CustomerServices.getCandidateDetail({ candidate_id: id });
      setCandidate(data);
      setVisaTenure(data?.visa_tenure || "1 year");
      setSelectedCountry(data?.nationality || null);
      setPassportExpiry(data?.passport_expiry ? new Date(data.passport_expiry) : null);
      setDocuments(mergeDocuments(data?.documents));
      reset({
        candidateName: data?.name,
        Phone: data?.phone,
        email: data?.email,
        campLocation: data?.camp_location,
        employeeid: data?.employee_id,
        nationality: data?.nationality?.name,
        passportNumber: data?.passport_number,
        passportExp: data?.passport_expiry ? moment(data.passport_expiry).format("YYYY-MM-DD") : "",
        visaDesignation: data?.visa_designation,
        basic: data?.salary_basic,
        allowance: data?.salary_allowance,
        totalSalary: data?.salary_total,
        endConsumer: data?.end_consumer,
        endConsumerCompany: data?.end_consumer_company,
      });
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && selectedItem?.id) {
      setSkipMedical(false);
      setSkipEid(false);
      setRequestDate(new Date());
      setRenewalDocument("");
      setPassportError(false);
      getCandidateDetail(selectedItem.id);
      if (countries.length === 0) getCountries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedItem?.id]);

  const totalSalaryCalc = () => {
    const basic = parseFloat(getValues("basic")) || 0;
    const allowance = parseFloat(getValues("allowance")) || 0;
    setValue("totalSalary", (basic + allowance).toFixed(2));
  };

  const verifyPassportNumber = async () => {
    const passportNumber = String(getValues("passportNumber") || "").trim().toUpperCase();
    if (!passportNumber || passportNumber === String(candidate?.passport_number || "").toUpperCase()) {
      setPassportError(false);
      return;
    }
    try {
      await CustomerServices.checkPassportNumber({ passport_number: passportNumber, candidate_id: candidate?.id });
      setPassportError(false);
    } catch (error) {
      setPassportError(true);
      showErrorToast(error);
    }
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append("document", file);
    const { data } = await instance.post(routes.uploadDocuments, formData);
    return data?.data?.path;
  };

  // Uploads the selected files and returns their paths joined by comma
  const uploadFiles = async (e, multiple) => {
    const inputElement = e.target;
    e.preventDefault();
    let path = "";
    try {
      const files = multiple ? Array.from(inputElement.files) : [inputElement.files[0]].filter(Boolean);
      for (const file of files) {
        if (!allowFilesType.includes(file.type)) {
          ErrorToaster(`Only ${CleanTypes(allowFilesType)} formats is supported`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          showErrorToast("File Size Must Be Less than 10 MB");
          continue;
        }
        const currentDate = new Date().toISOString().split("T")[0];
        const newFile = new File([file], `${currentDate}_${file.name}`, { type: file.type });
        const uploadedPath = await handleUpload(newFile);
        if (uploadedPath) path = path ? `${path},${uploadedPath}` : uploadedPath;
      }
    } catch (error) {
      ErrorToaster(error);
    } finally {
      inputElement.value = "";
    }
    return path;
  };

  const updateDocumentPath = (key, path) => {
    setDocuments((prev) => prev.map((doc) => (doc.key === key ? { ...doc, path } : doc)));
  };

  const handleClose = () => {
    setCandidate(null);
    setDocuments([]);
    reset({});
    onClose();
  };

  const submitRenewal = async (formData) => {
    if (!requestDate) {
      showErrorToast("Please select request date.");
      return;
    }
    if (!renewalDocument) {
      showErrorToast("Please upload document.");
      return;
    }
    if (passportError) {
      showErrorToast("Passport number is already used.");
      return;
    }
    const missingDoc = documents.find((doc) => doc.is_required && !doc.path);
    if (missingDoc) {
      showErrorToast(`Please upload ${missingDoc.name}.`);
      return;
    }

    const basic = parseFloat(formData?.basic) || 0;
    const allowance = parseFloat(formData?.allowance) || 0;

    const obj = {
      type: "Renew",
      candidate_id: candidate?.id,
      visa_id: candidate?.visa_id,
      document: renewalDocument,
      date: requestDate,
      skip_medical: skipMedical,
      skip_eid: skipEid,
      candidate: {
        // Keep the saved tenure text when the tenure is not changed
        visa_tenure: isTwoYear(visaTenure) === isTwoYear(candidate?.visa_tenure) ? candidate?.visa_tenure : visaTenure,
        phone: formData?.Phone,
        email: formData?.email,
        camp_location: formData?.campLocation,
        employee_id: formData?.employeeid,
        nationality_id: selectedCountry?.id,
        passport_number: String(formData?.passportNumber || "").trim().toUpperCase(),
        passport_expiry: moment(formData?.passportExp).format("YYYY-MM-DD"),
        visa_designation: formData?.visaDesignation,
        salary_basic: basic,
        salary_allowance: allowance,
        salary_total: (basic + allowance).toFixed(2),
        end_consumer: formData?.endConsumer,
        end_consumer_company: formData?.endConsumerCompany,
      },
      documents: documents,
    };

    try {
      const promise = VisaServices.visaProcessingAdd(obj);
      showPromiseToast(promise, "Saving...", "Added Successfully", "Something Went Wrong");
      const response = await promise;
      if (response?.responseCode === 200) {
        handleClose();
        onSaved && onSaved();
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} scroll="paper" maxWidth="md" fullWidth>
      <Box component="form" onSubmit={handleSubmit(submitRenewal)}>
        <DialogTitle>Renew Visa</DialogTitle>
        <DialogContent dividers>
          {loading || !candidate ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={"Request Date :*"}
                  value={requestDate}
                  disableFuture={true}
                  size={"small"}
                  error={!requestDate ? "Please select request date." : ""}
                  onChange={(date) => setRequestDate(date)}
                />
              </Grid>
              <Grid item xs={12} />

              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: "15px", color: Colors.black, mb: 1, fontWeight: "bold" }}>Visa Type : </Typography>
                <FormControl disabled>
                  <RadioGroup row value={String(candidate?.visa_type || "").toLowerCase() === "in" ? "In" : "out"}>
                    <FormControlLabel value="In" control={<Radio />} label="In" />
                    <FormControlLabel value="out" control={<Radio />} label="Out" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: "15px", color: Colors.black, mb: 1, fontWeight: "bold" }}>Visa Tenure : </Typography>
                <FormControl>
                  <RadioGroup
                    row
                    value={isTwoYear(visaTenure) ? "2 year" : "1 year"}
                    onChange={(e) => setVisaTenure(e.target.value)}
                  >
                    <FormControlLabel value="1 year" control={<Radio />} label="1 Year" />
                    <FormControlLabel value="2 year" control={<Radio />} label="2 Years" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: "15px", color: Colors.black, mb: 1, fontWeight: "bold" }}>Skip Medical : </Typography>
                <FormControl>
                  <RadioGroup row value={String(skipMedical)} onChange={(e) => setSkipMedical(e.target.value === "true")}>
                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: "15px", color: Colors.black, mb: 1, fontWeight: "bold" }}>Skip EID : </Typography>
                <FormControl>
                  <RadioGroup row value={String(skipEid)} onChange={(e) => setSkipEid(e.target.value === "true")}>
                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputField
                  label={"Candidate Name :"}
                  size={"small"}
                  fullWidth={true}
                  disabled={true}
                  placeholder={"Candidate Name"}
                  register={register("candidateName")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputField
                  label={"Phone :*"}
                  size={"small"}
                  fullWidth={true}
                  type={"number"}
                  placeholder={"Phone"}
                  error={errors?.Phone?.message}
                  register={register("Phone", {
                    required: "Please enter your Phone.",
                    pattern: {
                      value: /^05[0-9]{8}$/,
                      message: "Please enter a valid UAE phone number (starting with 05 and 8 digits).",
                    },
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputField
                  label={"Email :*"}
                  size={"small"}
                  fullWidth={true}
                  placeholder={"Email"}
                  error={errors?.email?.message}
                  register={register("email", { required: "Please enter your email." })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputField
                  label={"Camp Location :*"}
                  size={"small"}
                  fullWidth={true}
                  placeholder={"Camp Location"}
                  error={errors?.campLocation?.message}
                  register={register("campLocation", { required: "Please enter your camp location." })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputField
                  label={"Employee ID :*"}
                  size={"small"}
                  fullWidth={true}
                  placeholder={"Employee ID"}
                  error={errors?.employeeid?.message}
                  register={register("employeeid", { required: "Please enter your employee id." })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SelectField
                  size={"small"}
                  label={"Nationality :*"}
                  options={countries}
                  selected={selectedCountry}
                  onSelect={(value) => {
                    setSelectedCountry(value);
                    setValue("nationality", value?.name, { shouldValidate: true });
                  }}
                  error={errors?.nationality?.message}
                  register={register("nationality", { required: "Please select nationality" })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputField
                  label={"Passport Number :*"}
                  size={"small"}
                  fullWidth={true}
                  placeholder={"Passport Number"}
                  error={errors?.passportNumber?.message || (passportError ? "Passport number is already used." : "")}
                  register={register("passportNumber", {
                    required: "Please enter your passport number.",
                    onChange: () => Debounce2(() => verifyPassportNumber()),
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={"Passport Expiry :*"}
                  value={passportExpiry}
                  disablePast={true}
                  minDate={addDays(addMonths(new Date(), 6), 1)}
                  size={"small"}
                  error={errors?.passportExp?.message}
                  {...register("passportExp", {
                    required: "Please enter your passport expiry date.",
                    validate: (value) =>
                      moment(value) > moment(addMonths(new Date(), 6)) ||
                      "Passport expiry date must be at least 6 months from today.",
                  })}
                  onChange={(date) => {
                    setPassportExpiry(date);
                    setValue("passportExp", date, { shouldValidate: true });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputField
                  label={"Visa Designation :*"}
                  size={"small"}
                  fullWidth={true}
                  placeholder={"Visa Designation"}
                  error={errors?.visaDesignation?.message}
                  register={register("visaDesignation", { required: "Please enter your visa designation." })}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography sx={{ fontSize: "20px", fontWeight: "bold", color: Colors.textColorDarkBlue }}>Salary : </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelCustomInput
                  label={"Basic :* "}
                  StartLabel={"AED"}
                  placeholder={"Basic"}
                  error={errors?.basic?.message}
                  register={register("basic", { required: "Enter basic salary", onChange: () => totalSalaryCalc() })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelCustomInput
                  allowance={true}
                  label={"Allowance : "}
                  StartLabel={"AED"}
                  placeholder={"Allowance"}
                  error={errors?.allowance?.message}
                  register={register("allowance", { required: "Enter allowance", onChange: () => totalSalaryCalc() })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelCustomInput disabled={true} label={"Total Salary : "} StartLabel={"AED"} placeholder={"Total"} register={register("totalSalary")} />
              </Grid>
              <Grid item xs={12} sm={6} />
              <Grid item xs={12} sm={6}>
                <InputField
                  label={"End Consumer :*"}
                  size={"small"}
                  fullWidth={true}
                  placeholder={"End Consumer"}
                  error={errors?.endConsumer?.message}
                  register={register("endConsumer", { required: "Please enter your end consumer." })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputField
                  label={"End Consumer Company :*"}
                  size={"small"}
                  fullWidth={true}
                  placeholder={"End Consumer Company"}
                  error={errors?.endConsumerCompany?.message}
                  register={register("endConsumerCompany", { required: "Please enter your end consumer company." })}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography sx={{ fontSize: "20px", fontWeight: "bold", color: Colors.textColorDarkBlue }}>Documents : </Typography>
              </Grid>
              {documents.map((item) => (
                <Grid item xs={12} sm={6} key={item.key}>
                  <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: Colors.gray }}>
                    {item.is_required ? `${item.name} :*` : `${item.name} (If Any) :`}
                  </Typography>
                  <UploadFile
                    Memo={true}
                    accept={allowFilesType}
                    file={documents}
                    multiple={true}
                    updateResult={updateDocumentPath}
                    fileId={item.key}
                    loader={docLoader}
                    disabled={isUploading}
                    register={register(`doc_${item.key}`, {
                      onChange: async (e) => {
                        setIsUploading(true);
                        setDocLoader(item.key);
                        const path = await uploadFiles(e, true);
                        if (path) {
                          setDocuments((prev) =>
                            prev.map((doc) => (doc.key === item.key ? { ...doc, path: doc.path ? `${doc.path},${path}` : path } : doc))
                          );
                        }
                        setDocLoader(false);
                        setIsUploading(false);
                      },
                    })}
                  />
                </Grid>
              ))}

              <Grid item xs={12}>
                <Typography sx={{ fontSize: "20px", fontWeight: "bold", color: Colors.textColorDarkBlue }}>Renewal Request Document :* </Typography>
                <UploadFileSingle
                  Memo={true}
                  accept={allowFilesType}
                  file={renewalDocument}
                  register={register("renewalDocument", {
                    onChange: async (e) => {
                      const path = await uploadFiles(e, false);
                      if (path) setRenewalDocument(path);
                    },
                  })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button sx={{ fontFamily: "Public Sans", color: "#FF1F25" }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            sx={{ fontFamily: "Public Sans" }}
            type="submit"
            disabled={loading || !candidate || isUploading || !renewalDocument || !requestDate || passportError}
          >
            Submit Renewal
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default RenewVisaDialog;
